import { z } from 'zod';

// Mirrors backend's ONBOARDING_TAB_TITLE_MAX_LENGTH / ONBOARDING_TAB_PATH_MAX_LENGTH
// (backend/src/controllers/dtos/create-tenant-creation-request.dto.ts) —
// duplicated here because admin/backend are independent repos with no
// shared package; keep these two in sync if the backend limits change.
export const TAB_TITLE_MAX_LENGTH = 30;
export const TAB_PATH_MAX_LENGTH = 200;
export const MIN_TABS = 1;
export const MAX_TABS = 5;
export const ACCENT_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;
/** Active tab color until the merchant picks one. */
export const DEFAULT_ACCENT_COLOR = '#1860FB';

// Tabs that resolve to a screen already built into the app — their path is
// fixed internally and never typed/edited by the merchant (see
// `4-tab-bar-config.page.tsx`'s preset chips), so the full-URL requirement
// below doesn't apply to them.
export const PRESET_TABS = [
  { key: 'home', title: 'Início', path: '/', iconKey: 'home' },
  { key: 'cart', title: 'Carrinho', path: '/cart', iconKey: 'cart' },
  { key: 'search', title: 'Buscar', path: '/search', iconKey: 'search' },
];

export const INTERNAL_TAB_PATHS = new Set(
  PRESET_TABS.map((preset) => preset.path)
);

// Custom tabs open their link in a web view inside the app, so they need a
// full domain+path, not just a bare path — but, same as the store link on
// the previous step, the scheme is optional here: the merchant never types
// or sees "https://" (see `maskTabLink`/`normalizeTabLink`), it's added back
// internally before the value is submitted.
export const FULL_URL_REGEX =
  /^(?:https?:\/\/)?(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/\S*)?$/;

// Light input mask: strips protocol and whitespace as the merchant types —
// same pattern as AppNameAndLinkPage's maskStoreUrl — so the field always
// reads as a clean link instead of a pasted URL.
export function maskTabLink(value: string): string {
  return value
    .trim()
    .replace(/\s+/g, '')
    .replace(/^https?:\/\//, '');
}

// Adds the scheme back for submission — custom tabs are loaded in a web
// view, which needs the complete, absolute URL, even though the merchant
// never types or sees the "https://" prefix.
export function normalizeTabLink(value: string): string {
  return /^https?:\/\//.test(value) ? value : `https://${value}`;
}

export type TabLinkCheck =
  | { status: 'idle' }
  | { status: 'valid' }
  | { status: 'invalid'; message: string };

// Live, field-level feedback shown as the merchant types a custom tab's
// link — same idea as AppNameAndLinkPage's checkStoreUrl, separate from the
// zod schema below (which still gates actual submission).
export function checkTabLink(value: string): TabLinkCheck {
  // Too short to give meaningful feedback yet — avoids flashing an error
  // the instant the merchant types the first character or two.
  if (value.length < 4) {
    return { status: 'idle' };
  }

  if (FULL_URL_REGEX.test(value)) {
    return { status: 'valid' };
  }

  if (!value.includes('.')) {
    return {
      status: 'invalid',
      message: 'Falta a extensão do domínio, como .com ou .com.br',
    };
  }

  if (!/\.[a-zA-Z]{2,}/.test(value)) {
    return {
      status: 'invalid',
      message: 'Falta a extensão do domínio, como .com ou .com.br',
    };
  }

  return {
    status: 'invalid',
    message: 'Verifique se o link está completo e correto',
  };
}

export const tabConfigSchema = z
  .object({
    key: z.string().min(1),
    title: z
      .string()
      .min(1, 'Dê um nome a esta aba')
      .max(
        TAB_TITLE_MAX_LENGTH,
        `Máximo de ${TAB_TITLE_MAX_LENGTH} caracteres`
      ),
    path: z
      .string()
      .min(1, 'Informe o link desta aba')
      .max(TAB_PATH_MAX_LENGTH, `Máximo de ${TAB_PATH_MAX_LENGTH} caracteres`),
    iconKey: z.string().min(1, 'Selecione um ícone'),
  })
  .superRefine((tab, ctx) => {
    // Preset tabs keep their fixed internal path — only a custom tab's
    // merchant-typed link has to look like a complete URL.
    if (INTERNAL_TAB_PATHS.has(tab.path)) {
      return;
    }

    if (!FULL_URL_REGEX.test(tab.path)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['path'],
        message: 'Link incompleto — tente algo como sualoja.com.br/pagina',
      });
    }
  })
  // Scheme-less in the form (see `maskTabLink`), but the web view that opens
  // a custom tab needs the absolute URL — added back here, once, right
  // before the value leaves the form.
  .transform((tab) => ({
    ...tab,
    path: INTERNAL_TAB_PATHS.has(tab.path)
      ? tab.path
      : normalizeTabLink(tab.path),
  }));

export const TabBarConfigFormSchema = z.object({
  tabs: z
    .array(tabConfigSchema)
    .min(MIN_TABS, 'Adicione ao menos 1 tab')
    .max(MAX_TABS, `No máximo ${MAX_TABS} tabs`),
  accentColor: z
    .string()
    .regex(ACCENT_COLOR_REGEX, 'Cor inválida. Use o formato #RRGGBB'),
});

export type TabConfigFormValues = z.infer<typeof tabConfigSchema>;
export type TabBarConfigFormValues = z.infer<typeof TabBarConfigFormSchema>;
